import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import React from 'react';

import { TabContextMenuProps } from './TabContextMenu.props';

/**
 * A styled (non-native) right-click menu shared by the default tab and the
 * pinned report tabs. Positioned at the pointer via anchorReference, driven by
 * a plain items array so each caller decides which rows to show, their order,
 * and which are disabled.
 */
export const TabContextMenu: React.FC<TabContextMenuProps> = ({
  anchorPosition,
  onClose,
  items,
}) => {
  const theme = useTheme();
  const open = anchorPosition !== null;

  return (
    <Menu
      open={open}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition ?? undefined}
      slotProps={{ paper: { sx: { minWidth: 220 } } }}
    >
      {items.map((item) => {
        const row = (
          <MenuItem
            key={item.key}
            disabled={item.disabled}
            onClick={() => {
              if (item.disabled) return;
              item.onClick?.();
              onClose();
            }}
            sx={{ fontSize: '0.8125rem' }}
          >
            <ListItemText
              primary={item.label}
              secondary={item.hint}
              slotProps={{
                primary: { sx: { fontSize: '0.8125rem' } },
                secondary: {
                  sx: {
                    fontSize: '0.6875rem',
                    fontStyle: 'italic',
                    color: theme.palette.text.disabled,
                  },
                },
              }}
            />
          </MenuItem>
        );
        // MUI's Menu needs MenuItem children as direct descendants for keyboard
        // navigation, so emit the divider as a sibling rather than a wrapper.
        return item.dividerBefore
          ? [<Divider key={`${item.key}-divider`} sx={{ my: 0.5 }} />, row]
          : row;
      })}
    </Menu>
  );
};
